import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  Stack, Chip, IconButton, Tooltip, InputAdornment, TextField, MenuItem,
  Button, Divider, Badge,
} from '@mui/material';
import NotificationsIcon  from '@mui/icons-material/Notifications';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import DoneAllIcon        from '@mui/icons-material/DoneAll';
import DeleteOutlineIcon  from '@mui/icons-material/DeleteOutline';
import ErrorIcon          from '@mui/icons-material/Error';
import WarningIcon        from '@mui/icons-material/Warning';
import InfoIcon           from '@mui/icons-material/Info';
import CheckCircleIcon    from '@mui/icons-material/CheckCircle';
import SearchIcon         from '@mui/icons-material/Search';
import apiService from '../services/apiServices';

const API = process.env.REACT_APP_API_URL || '';
function auth() {
  try {
    const raw = sessionStorage.getItem('logedInUser');
    if (raw) return JSON.parse(raw)?.token || '';
    const ck = document.cookie.split(';').find(c => c.trim().startsWith('logedInUser='));
    if (ck) return JSON.parse(decodeURIComponent(ck.split('=').slice(1).join('=')))?.token || '';
    return JSON.parse(localStorage.getItem('logedInUser') || '{}')?.token || '';
  } catch { return ''; }
}

const TYPE_CFG = {
  critical: { icon: <ErrorIcon fontSize="small" />,   color: 'error',   label: 'Kritik' },
  high:     { icon: <WarningIcon fontSize="small" />,  color: 'error',   label: 'I Lartë' },
  medium:   { icon: <WarningIcon fontSize="small" />,  color: 'warning', label: 'Mesatar' },
  low:      { icon: <InfoIcon fontSize="small" />,     color: 'info',    label: 'Info' },
  info:     { icon: <InfoIcon fontSize="small" />,     color: 'info',    label: 'Info' },
  success:  { icon: <CheckCircleIcon fontSize="small" />, color: 'success', label: 'Sukses' },
};

function fmtDate(s) {
  if (!s) return '';
  try { return new Date(s).toLocaleDateString('sq-AL', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return s; }
}

export default function NotificationsCenterPage() {
  const [notifs,   setNotifs]   = useState([]);
  const [search,   setSearch]   = useState('');
  const [typeF,    setTypeF]    = useState('');
  const [readF,    setReadF]    = useState('all'); // 'all' | 'unread' | 'read'

  const load = useCallback(() => {
    apiService().query('getNotifications', '').then(r => {
      if (r?.success) setNotifs(r.data || []);
    }).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => notifs.filter(n => {
    const mS = !search || (n.Title||'').toLowerCase().includes(search.toLowerCase()) || (n.Message||'').toLowerCase().includes(search.toLowerCase()) || (n.Category||'').toLowerCase().includes(search.toLowerCase());
    const mT = !typeF  || (n.Type||'').toLowerCase() === typeF;
    const mR = readF === 'all' ? true : readF === 'unread' ? !n.IsRead : n.IsRead;
    return mS && mT && mR;
  }), [notifs, search, typeF, readF]);

  const unreadCount = useMemo(() => notifs.filter(n => !n.IsRead).length, [notifs]);

  const markAsRead = async (n) => {
    if (n.IsRead) return;
    await apiService().put('notifications', { ...n, IsRead: true });
    setNotifs(prev => prev.map(x => Number(x.Id) === Number(n.Id) ? { ...x, IsRead: true } : x));
  };

  const markAllRead = async () => {
    const unread = notifs.filter(n => !n.IsRead);
    await Promise.all(unread.map(n => apiService().put('notifications', { ...n, IsRead: true })));
    setNotifs(prev => prev.map(n => ({ ...n, IsRead: true })));
  };

  const deleteNotif = async (n) => {
    await fetch(`${API}/api/notifications/${n.Id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${auth()}` } });
    setNotifs(prev => prev.filter(x => Number(x.Id) !== Number(n.Id)));
  };

  return (
    <Box>
      {/* Summary row */}
      <Stack direction="row" spacing={2} mb={2} alignItems="center" flexWrap="wrap">
        <Badge badgeContent={unreadCount} color="error">
          <ChipStat icon={<NotificationsIcon />} label={`${notifs.length} gjithsej`} />
        </Badge>
        {unreadCount > 0 && (
          <Chip icon={<NotificationsIcon />} label={`${unreadCount} të palexuara`} color="warning" sx={{ fontWeight: 700 }} />
        )}
        <Box flex={1} />
        {unreadCount > 0 && (
          <Button size="small" startIcon={<DoneAllIcon />} onClick={markAllRead} variant="outlined">
            Shëno të gjitha si të lexuara
          </Button>
        )}
      </Stack>

      {/* Filters */}
      <Stack direction="row" spacing={1} mb={2} flexWrap="wrap">
        <TextField size="small" placeholder="Kërko…" value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ width: 220 }} />
        <TextField select size="small" label="Tipi" value={typeF} onChange={e => setTypeF(e.target.value)} sx={{ width: 140 }}>
          <MenuItem value="">Të gjithë</MenuItem>
          {Object.entries(TYPE_CFG).map(([k,v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Statusi" value={readF} onChange={e => setReadF(e.target.value)} sx={{ width: 150 }}>
          <MenuItem value="all">Të gjithë</MenuItem>
          <MenuItem value="unread">Të palexuara</MenuItem>
          <MenuItem value="read">Të lexuara</MenuItem>
        </TextField>
        <Box flex={1} />
        <Typography variant="body2" color="text.secondary" alignSelf="center">{filtered.length} njoftim(e)</Typography>
      </Stack>

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell width={40}></TableCell>
              <TableCell width={100}>Tipi</TableCell>
              <TableCell>Titulli / Mesazhi</TableCell>
              <TableCell width={100}>Kategoria</TableCell>
              <TableCell width={150}>Data</TableCell>
              <TableCell width={80}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                <NotificationsOffIcon sx={{ fontSize: 48, opacity: 0.3, display: 'block', mx: 'auto', mb: 1 }} />
                Nuk ka njoftime{(search||typeF||readF !== 'all') ? ' për filtrat e zgjedhur' : ''}.
              </TableCell></TableRow>
            )}
            {filtered.map(n => {
              const cfg = TYPE_CFG[n.Type||'info'] || TYPE_CFG.info;
              return (
                <TableRow key={n.Id} hover sx={{ bgcolor: n.IsRead ? undefined : 'action.selected', opacity: n.IsRead ? 0.8 : 1 }}>
                  <TableCell sx={{ color: `${cfg.color}.main` }}>{cfg.icon}</TableCell>
                  <TableCell><Chip size="small" label={cfg.label} color={cfg.color} /></TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={n.IsRead ? 400 : 600}>{n.Title}</Typography>
                    {n.Message && <Typography variant="caption" color="text.secondary">{n.Message}</Typography>}
                    {n.Link && <Typography variant="caption" color="primary" display="block">{n.Link}</Typography>}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{n.Category||'—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{fmtDate(n.CreatedAt)}</Typography>
                  </TableCell>
                  <TableCell>
                    {!n.IsRead && (
                      <Tooltip title="Shëno si të lexuar">
                        <IconButton size="small" onClick={() => markAsRead(n)}><CheckCircleIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Fshij">
                      <IconButton size="small" onClick={() => deleteNotif(n)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}

function ChipStat({ icon, label }) {
  return <Chip icon={icon} label={label} variant="outlined" />;
}
